import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { toInputDateFormat } from '../../lib/utils';
import { createReleaseNote, updateReleaseNote } from '../../modules/admin';
import { RELEASE_NOTE_ITEM_ALLOWED_TYPES } from '../../static/common';
import ConfirmationModal from '../elements/ConfirmationModal';
import Modal from '../elements/Modal';
import TextInput from '../elements/TextInput';
import { useNotification } from '../../hooks/NotificationContext';
const defaultItem = { type: '', title: '', description: '' };
const currentDate = new Date().toISOString().split('T')[0];

const defaultValues = {
  version: 'v',
  title: '',
  releasedAt: currentDate,
  published: false,
  description: '',
  items: [defaultItem],
};

const CreateReleaseNoteModal = ({
  showModal,
  handleClose,
  refreshList,
  initialData,
  mode,
}) => {
  const isEditMode = mode === 'edit' ? true : false;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const isPublished = initialData?.published;
  const { addNotification } = useNotification();

  const loadInitialValues = useCallback(() => {
    if (isEditMode && initialData) {
      setInitialValues({
        version: initialData.version?.startsWith('v')
          ? initialData.version
          : `v${initialData.version || ''}`,
        title: initialData.title || '',
        releasedAt: toInputDateFormat(initialData.released_at) || currentDate,
        published: initialData.published || false,
        description: initialData.description || '',
        items: initialData.items?.length ? initialData.items : [defaultItem],
      });
    } else {
      setInitialValues(defaultValues);
    }
  }, [isEditMode, initialData]);

  useEffect(() => {
    if (showModal) loadInitialValues();
  }, [showModal, loadInitialValues]);

  const handleSave = async (values, shouldPublish, resetForm) => {
    const filteredItems = values.items.filter(
      (item) => item.type?.trim() && item.title?.trim()
    );
    const payload = {
      ...values,
      items: filteredItems,
      published: shouldPublish,
    };
    try {
      isEditMode
        ? await updateReleaseNote(initialData.id, payload)
        : await createReleaseNote(payload);
      addNotification('success', 'Release note updated successfully!');
      refreshList?.();
      resetForm();
      handleDiscard();
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to update release note.');
    }
  };

  const handleDiscard = () => {
    setShowConfirmationModal(false);
    handleClose();
    setUploadError(null);
    setInitialValues(defaultValues);
  };

  if (!initialValues) return null;

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={() => {}}>
        {({ values, setFieldValue, setValues, isSubmitting, resetForm }) => (
          <Modal
            open={showModal}
            width={'lg'}
            handleClose={() => setShowConfirmationModal(true)}
            modalTitle={
              isEditMode ? 'Edit Release Note' : 'Create Release Note'
            }
            action={() => (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setShowConfirmationModal(true)}
                  disabled={isSubmitting}>
                  Close
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleSave(values, false, resetForm)}
                  disabled={isSubmitting || isPublished}>
                  Save as Draft
                </Button>

                <Button
                  variant="contained"
                  onClick={() => handleSave(values, true, resetForm)}
                  disabled={
                    !/^v[0-9A-Za-z].*/.test(values.version?.trim() || '') ||
                    !values.title?.trim() ||
                    isSubmitting
                  }>
                  {isSubmitting ? 'Saving...' : 'Save & Publish'}
                </Button>
              </>
            )}>
            <Form>
              <Grid container spacing={1}>
                {!isEditMode && (
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <InputLabel>
                        Upload JSON File (
                        <a href={'/sampleReleaseNote.json'} download>
                          Download format
                        </a>
                        )
                      </InputLabel>{' '}
                      <input
                        type="file"
                        accept=".json"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;

                          const reader = new FileReader();
                          reader.onload = (event) => {
                            try {
                              const json = JSON.parse(event.target.result);

                              const isValid =
                                typeof json.version === 'string' &&
                                typeof json.title === 'string' &&
                                typeof json.releasedAt === 'string' &&
                                Array.isArray(json.items) &&
                                json.items.every(
                                  (item) =>
                                    typeof item.type === 'string' &&
                                    typeof item.title === 'string' &&
                                    (typeof item.description === 'string' ||
                                      item.description === undefined)
                                );

                              if (!isValid) {
                                setUploadError(
                                  'This file does not have a valid structure. Please upload a valid JSON file.'
                                );
                                return;
                              }

                              setValues({
                                version: json.version.startsWith('v')
                                  ? json.version
                                  : `v${json.version}`,
                                title: json.title,
                                releasedAt: json.releasedAt || currentDate,
                                published: json.published || false,
                                description: json.description || '',
                                items: json.items.length
                                  ? json.items
                                  : [defaultItem],
                              });
                              setUploadError(null);
                            } catch (err) {
                              console.error(err);
                              setUploadError('Unable to parse JSON file.');
                            }
                          };
                          reader.readAsText(file);
                        }}
                      />
                      {uploadError && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {uploadError}
                        </Alert>
                      )}
                    </Stack>
                  </Grid>
                )}
              </Grid>
              {!isEditMode && <hr />}
              <h6>Release Details</h6>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <InputLabel className="required">Version</InputLabel>
                    <TextInput
                      id="version"
                      value={values.version}
                      onChange={(id, value) => {
                        const updated = value.startsWith('v')
                          ? value
                          : `v${value.replace(/^v*/, '')}`;
                        setFieldValue('version', updated);
                      }}
                      placeholder="e.g. v1.2.3"
                      maxLength={25}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <InputLabel className="required">Release Date</InputLabel>
                    <TextField
                      type="date"
                      id="releasedAt"
                      value={values.releasedAt}
                      onChange={(e) =>
                        setFieldValue('releasedAt', e.target.value)
                      }
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={values?.published}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel className="required">Title</InputLabel>
                    <TextInput
                      id="title"
                      value={values.title}
                      onChange={(id, value) => setFieldValue(id, value)}
                      maxLength={50}
                      placeholder="Release Title"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <Stack spacing={1}>
                    <InputLabel>Description</InputLabel>
                    <TextInput
                      id="description"
                      value={values.description}
                      onChange={(id, value) => setFieldValue(id, value)}
                      maxLength={255}
                      placeholder="Release description"
                      type="textarea"
                      rows={4}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <FieldArray name="items">
                    {({ push, remove }) => (
                      <Stack>
                        {RELEASE_NOTE_ITEM_ALLOWED_TYPES.map(
                          (type, sectionIdx) => {
                            const itemsForType = values.items
                              .map((item, i) => ({ ...item, index: i }))
                              .filter((item) => item.type === type);

                            return (
                              <>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  key={sectionIdx}>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold">
                                    {type}
                                  </Typography>
                                  <Tooltip title={`Add ${type}`}>
                                    <IconButton
                                      color="primary"
                                      sx={{ ml: 2 }}
                                      onClick={() =>
                                        push({
                                          type,
                                          title: '',
                                          description: '',
                                        })
                                      }>
                                      <AddCircleOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Grid container spacing={2}>
                                  {itemsForType?.length === 0 ? (
                                    <Grid item xs={12}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontStyle="italic">
                                        No items added yet.
                                      </Typography>
                                    </Grid>
                                  ) : (
                                    itemsForType.map((item) => (
                                      <React.Fragment key={item.index}>
                                        <Grid item xs={4}>
                                          <TextInput
                                            id={`items[${item.index}].title`}
                                            value={item.title}
                                            onChange={(id, value) =>
                                              setFieldValue(id, value)
                                            }
                                            rows={2}
                                            type="textarea"
                                            placeholder={`${type} Title`}
                                            maxLength={50}
                                          />
                                        </Grid>
                                        <Grid item xs={7}>
                                          <TextInput
                                            id={`items[${item.index}].description`}
                                            value={item.description}
                                            onChange={(id, value) =>
                                              setFieldValue(id, value)
                                            }
                                            placeholder={`${type} Description`}
                                            maxLength={150}
                                            rows={2}
                                            type="textarea"
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          xs={1}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}>
                                          <IconButton
                                            color="error"
                                            title="Remove"
                                            onClick={() => remove(item.index)}>
                                            <RemoveCircleOutline fontSize="small" />
                                          </IconButton>
                                        </Grid>
                                      </React.Fragment>
                                    ))
                                  )}
                                </Grid>
                              </>
                            );
                          }
                        )}
                      </Stack>
                    )}
                  </FieldArray>
                </Grid>
              </Grid>
            </Form>
          </Modal>
        )}
      </Formik>

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={handleDiscard}
        title="Discard Changes?"
        body="Are you sure you want to cancel? Unsaved changes will be lost."
        buttonLabel="Discard"
        buttonLabelSecondary="Go Back"
        buttonStyle="primary"
      />
    </>
  );
};

export default CreateReleaseNoteModal;
